import React, { useState } from 'react'
import { manuals } from '../../data/manuals'
import { toast } from 'sonner'
import { Book, Code, Bot, FileText, Copy, Check, Download, Package } from 'lucide-react'

// Archivos descargables del kit (importados como raw text)
import vendorCoreSrc from '../../vendor_core.ts?raw'
import buildDrmSrc from '../../../scripts/build_drm.cjs?raw'

// Simple Markdown-ish renderer
function MarkdownViewer({ content }) {
    let inCodeBlock = false
    let codeBuffer = []
    let codeLang = ''

    const elements = []
    const lines = content.split('\n')

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i]

        if (line.startsWith('```')) {
            if (inCodeBlock) {
                const code = codeBuffer.join('\n')
                elements.push(
                    <div key={`code-${i}`} className="relative group my-3">
                        <button
                            onClick={() => { navigator.clipboard.writeText(code); toast.success('Código copiado') }}
                            className="absolute top-2 right-2 p-1.5 rounded bg-white/5 text-gray-500 hover:text-white hover:bg-white/10 opacity-0 group-hover:opacity-100 transition-all"
                            aria-label="Copiar código"
                        >
                            <Copy size={12} />
                        </button>
                        <pre className="bg-charcoal p-4 rounded-lg font-mono text-xs border border-white/10 overflow-x-auto text-green-400 whitespace-pre-wrap">
                            {code}
                        </pre>
                    </div>
                )
                codeBuffer = []
                inCodeBlock = false
            } else {
                codeLang = line.replace('```', '').trim()
                inCodeBlock = true
            }
            continue
        }

        if (inCodeBlock) {
            codeBuffer.push(line)
            continue
        }

        if (line.startsWith('# ')) {
            elements.push(<h1 key={i} className="text-3xl font-bold text-white mt-8 mb-4 border-b border-gray-700 pb-2">{line.replace('# ', '')}</h1>)
        } else if (line.startsWith('## ')) {
            elements.push(<h2 key={i} className="text-2xl font-bold text-neon-blue mt-6 mb-3">{line.replace('## ', '')}</h2>)
        } else if (line.startsWith('### ')) {
            elements.push(<h3 key={i} className="text-xl font-semibold text-white mt-4 mb-2">{line.replace('### ', '')}</h3>)
        } else if (line.startsWith('---')) {
            elements.push(<hr key={i} className="border-gray-700 my-4" />)
        } else if (line.startsWith('| ')) {
            const tableLines = [line]
            while (i + 1 < lines.length && lines[i + 1].startsWith('|')) {
                i++
                tableLines.push(lines[i])
            }
            const headerCells = tableLines[0].split('|').filter(c => c.trim())
            const dataRows = tableLines.slice(2)
            elements.push(
                <div key={`table-${i}`} className="overflow-x-auto my-3">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-white/10">
                                {headerCells.map((c, ci) => (
                                    <th key={ci} className="text-left px-3 py-2 text-gray-400 font-medium">{c.trim()}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {dataRows.map((row, ri) => (
                                <tr key={ri} className="border-b border-white/5">
                                    {row.split('|').filter(c => c.trim()).map((c, ci) => (
                                        <td key={ci} className="px-3 py-2 text-gray-300 font-mono text-xs">{c.trim()}</td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )
        } else if (line.startsWith('- ')) {
            elements.push(<li key={i} className="ml-4 list-disc marker:text-neon-blue">{line.replace('- ', '')}</li>)
        } else if (line.startsWith('> ')) {
            elements.push(<blockquote key={i} className="border-l-2 border-neon-blue pl-4 py-1 text-gray-400 italic my-2">{line.replace('> ', '')}</blockquote>)
        } else if (line.startsWith('⚠️') || line.startsWith('❌') || line.startsWith('✅')) {
            elements.push(<p key={i} className="leading-relaxed whitespace-pre-wrap font-medium text-yellow-400">{line}</p>)
        } else if (line.trim() === '') {
            elements.push(<div key={i} className="h-2" />)
        } else {
            elements.push(<p key={i} className="leading-relaxed whitespace-pre-wrap">{line}</p>)
        }
    }

    return <div className="prose prose-invert max-w-none text-gray-300 space-y-1">{elements}</div>
}

function downloadFile(content, filename) {
    const blob = new Blob([content], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.click()
    URL.revokeObjectURL(url)
    toast.success(`${filename} descargado`)
}

export default function DocsPage() {
    const [activeManual, setActiveManual] = useState(manuals[0])
    const [copied, setCopied] = useState(false)

    const icons = {
        Book: <Book size={18} />,
        Code: <Code size={18} />,
        Bot: <Bot size={18} />,
        Package: <Package size={18} />
    }

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(activeManual.content)
            setCopied(true)
            toast.success('Manual copiado al portapapeles')
            setTimeout(() => setCopied(false), 2000)
        } catch {
            toast.error('No se pudo copiar')
        }
    }

    // Archivos descargables del kit
    const KIT_FILES = [
        { name: 'vendor_core.js', size: '18 KB', desc: 'Agente espía (universal)', content: vendorCoreSrc, icon: '🛡️' },
        { name: 'build_drm.cjs', size: '3 KB', desc: 'Script de ofuscación', content: buildDrmSrc, icon: '🔒' },
        { name: 'INSTRUCCIONES.md', size: '6 KB', desc: 'Manual para el agente IA', content: manuals.find(m => m.id === 'kit')?.content || '', icon: '📋' }
    ]

    // For the new navigation structure, let's adapt `manuals` to `guides` and `activeManual` to `activeId`
    const guides = manuals.map(man => ({
        id: man.id,
        title: man.title,
        icon: icons[man.icon] ? () => icons[man.icon] : () => <FileText size={18} /> // Adapt icon to be a component
    }));
    const [activeId, setActiveId] = useState(manuals[0].id);

    // Update activeManual when activeId changes
    React.useEffect(() => {
        const foundManual = manuals.find(m => m.id === activeId);
        if (foundManual) {
            setActiveManual(foundManual);
            setCopied(false);
        }
    }, [activeId]);


    return (
        <div className="h-[calc(100vh-64px)] flex flex-col lg:flex-row">
            {/* Selector Móvil */}
            <div className="lg:hidden p-4 border-b border-white/5 bg-charcoal">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-2 block">Seleccionar Manual</label>
                <div className="relative">
                    <select
                        value={activeId}
                        onChange={(e) => setActiveId(e.target.value)}
                        className="w-full appearance-none bg-black/30 border border-white/10 rounded-lg py-3 px-4 text-white focus:outline-none focus:border-neon-blue pr-10"
                    >
                        {guides.map(guide => (
                            <option key={guide.id} value={guide.id}>{guide.title}</option>
                        ))}
                    </select>
                </div>
            </div>
            {/* Sidebar de Navegación (Desktop) */}
            <aside className="hidden lg:block w-64 shrink-0 space-y-2 border-r border-white/5 bg-charcoal/30 flex flex-col">
                <div className="p-4 flex-1">
                    <div className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-4">Manuales</div>
                    {guides.map(guide => (
                        <button
                            key={guide.id}
                            onClick={() => setActiveId(guide.id)}
                            className={`w-full text-left px-3 py-2 rounded-lg flex items-center gap-3 transition-colors ${activeId === guide.id
                                ? 'bg-neon-blue/10 text-neon-blue border border-neon-blue/20'
                                : 'text-gray-400 hover:bg-white/5 hover:text-white'
                                }`}
                            aria-label={`Ver ${guide.title}`}
                        >
                            {guide.icon()}
                            <span className="text-sm font-medium truncate">{guide.title}</span>
                        </button>
                    ))}
                </div>

                {/* Kit de Descarga */}
                <div className="p-4 border-t border-white/5">
                    <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                        <Download size={14} /> Kit de Integración
                    </h3>
                    <div className="space-y-2">
                        {KIT_FILES.map(file => (
                            <button
                                key={file.name}
                                onClick={() => downloadFile(file.content, file.name)}
                                className="w-full text-left px-3 py-2 rounded-lg flex items-center gap-2 text-gray-400 hover:bg-neon-green/5 hover:text-neon-green transition-colors group border border-transparent hover:border-neon-green/20"
                                aria-label={`Descargar ${file.name}`}
                            >
                                <span className="text-base">{file.icon}</span>
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs font-mono truncate">{file.name}</p>
                                    <p className="text-[10px] text-gray-600">{file.desc} · {file.size}</p>
                                </div>
                                <Download size={12} className="opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                            </button>
                        ))}
                    </div>
                </div>
            </aside>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-10 bg-obsidian">
                <div className="max-w-4xl mx-auto">
                    {/* Action buttons */}
                    <div className="flex justify-end gap-2 mb-4">
                        <button
                            onClick={handleCopy}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${copied
                                ? 'bg-neon-green/10 text-neon-green border border-neon-green/20'
                                : 'bg-neon-blue/10 text-neon-blue border border-neon-blue/20 hover:bg-neon-blue/20'
                                }`}
                            aria-label="Copiar manual al portapapeles"
                        >
                            {copied ? <Check size={16} /> : <Copy size={16} />}
                            {copied ? '¡Copiado!' : 'Copiar Manual'}
                        </button>
                    </div>

                    <div className="glass-panel p-8 rounded-xl min-h-full">
                        <MarkdownViewer content={activeManual.content} />
                    </div>
                </div>
            </div>
        </div >
    )
}
